import { VonqContractPostingRequirements } from 'api-clients/monolith/models/VonqContractPostingRequirements';

type PostingRequirementFieldDef =
  VonqContractPostingRequirements['posting_requirements'][number];

type DisplayRule = NonNullable<
  PostingRequirementFieldDef['display_rules']
>['show'][number];

type ContractValues = Record<string, string | string[] | null | undefined>;

/**
 * Options loaded at runtime from the autocomplete API, keyed by field name.
 * Each entry mirrors the structure of a static `field.options` entry but comes
 * from the lazy-load fetch — where `field.options` in the contract definition
 * is empty but `show[]` metadata is carried in the API response.
 */
export type LoadedOptions = Record<
  string,
  Array<{ key: string | null; show?: string[] | null }>
>;

function evalRule(
  rule: DisplayRule,
  contractValues: ContractValues,
  allFields: PostingRequirementFieldDef[],
  fieldName: string,
  loadedOptions?: LoadedOptions,
): boolean {
  const facetValue = contractValues[rule.facet];
  const isArray = Array.isArray(facetValue);
  const strValue = isArray ? '' : String(facetValue ?? '').trim();

  switch (rule.op) {
    case 'notempty':
      return isArray ? facetValue.length > 0 : strValue.length > 0;

    case 'equal': {
      const target = String(rule.value ?? '');
      return isArray ? facetValue.includes(target) : strValue === target;
    }

    case 'in': {
      // rule.value is a single scalar; check whether it appears among the
      // facet field's current selected values.
      const target = String(rule.value ?? '');
      return isArray ? facetValue.includes(target) : strValue === target;
    }

    case 'contains': {
      const target = String(rule.value ?? '');
      if (isArray) return facetValue.includes(target);
      return strValue.includes(target);
    }

    case 'selected_option_show_contains': {
      // When value is null the server means: use this field's own name as the
      // lookup target — show this field only when the selected option explicitly
      // lists it in its `show` array. A non-null value is used as-is.
      const target = rule.value !== null ? String(rule.value) : fieldName;

      // Find the facet field's definition to verify it exists.
      const facetField = allFields.find(f => f.name === rule.facet);
      if (!facetField) return true; // unknown facet — show by default

      // Prefer loaded options from the autocomplete API response: for lazy-load
      // fields the contract's field.options is empty, but the API response
      // carries the show[] metadata needed to evaluate this rule.
      const optionSource: Array<{
        key: string | null;
        show?: string[] | null;
      }> = loadedOptions?.[rule.facet]?.length
        ? loadedOptions[rule.facet]
        : facetField.options;

      const selectedKeys = isArray ? facetValue : strValue ? [strValue] : [];

      return selectedKeys.some(selectedKey => {
        const option = optionSource.find(o => o.key === selectedKey);
        return option?.show?.includes(target) ?? false;
      });
    }

    default:
      return true;
  }
}

/**
 * Returns true when the field should be visible to the user based on its
 * display_rules and the current values of sibling fields in the same contract.
 *
 * - display_rules === null  → always show
 * - otherwise               → show if at least one `show` rule matches
 *
 * @param field          The posting requirement field to evaluate.
 * @param contractValues Current form values keyed by field name for this contract.
 * @param allFields      All posting_requirements for this contract; required to
 *                       resolve facet field options for selected_option_show_contains.
 * @param loadedOptions  Options loaded at runtime from the autocomplete API,
 *                       keyed by field name. Required for lazy-load facet fields
 *                       whose contract field.options is empty but whose API
 *                       response carries option.show[] metadata.
 */
export function shouldShowField(
  field: PostingRequirementFieldDef,
  contractValues: ContractValues,
  allFields: PostingRequirementFieldDef[],
  loadedOptions?: LoadedOptions,
): boolean {
  if (!field.display_rules) return true;
  return field.display_rules.show.some(rule =>
    evalRule(rule, contractValues, allFields, field.name, loadedOptions),
  );
}
